import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Edit, Trash2, X, Check, Clock, DollarSign, Package, RefreshCw, ExternalLink, Users } from "lucide-react";
import { toast } from "sonner";
import { API } from "./SharedComponents";

const ProductsManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/products`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error("Failed to fetch products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const syncToStripe = async (productId) => {
    setSyncing(productId);
    try {
      const response = await fetch(`${API}/products/${productId}/sync-stripe`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Product synced to Stripe!");
        fetchProducts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to sync");
      }
    } catch (error) {
      toast.error("Sync failed");
    } finally {
      setSyncing(null);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to deactivate this product?")) return;
    
    try {
      const response = await fetch(`${API}/products/${productId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Product deactivated");
        fetchProducts();
      } else {
        toast.error("Failed to deactivate product");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const filteredProducts = products.filter(p => {
    if (filter === "all") return true;
    return p.product_type === filter;
  });

  const formatPrice = (amount, currency, interval) => {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase()
    }).format(amount);
    
    if (interval) {
      return `${formatted}/${interval}`;
    }
    return formatted;
  };

  const getProductTypeColor = (type) => {
    switch (type) {
      case "subscription": return "bg-primary/20 text-primary";
      case "one_time": return "bg-lime-400/20 text-lime-300";
      case "coaching_package": return "bg-purple-500/20 text-purple-400";
      default: return "bg-[#2D3B4F] text-zinc-300";
    }
  };

  const getProductTypeLabel = (type) => {
    switch (type) {
      case "subscription": return "Subscription";
      case "one_time": return "One-Time";
      case "coaching_package": return "Coaching Package";
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Products & Pricing</h2>
          <p className="text-gray-400 mt-1">Manage your subscriptions, workout packs, and coaching packages</p>
        </div>
        <button
          onClick={() => { setEditingProduct(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          data-testid="create-product-btn"
        >
          <Plus className="w-5 h-5" />
          Create Product
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { id: "all", label: "All Products" },
          { id: "subscription", label: "Subscriptions" },
          { id: "one_time", label: "One-Time" },
          { id: "coaching_package", label: "Coaching Packages" }
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.id
                ? "bg-[#2D3B4F] text-white"
                : "bg-[#141B2D] text-gray-400 hover:bg-[#1E293B]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <p className="text-white font-medium">Stripe Connected</p>
            <p className="text-gray-400 text-sm">Products auto-sync to your Stripe account</p>
          </div>
        </div>
        <a
          href="https://dashboard.stripe.com/products"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm"
        >
          View in Stripe <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="text-center py-16 bg-[#141B2D] rounded-xl border border-[#1E293B]">
          <Package className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Products Yet</h3>
          <p className="text-gray-400 mb-6">Create your first product to start selling</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Create Your First Product
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => (
            <motion.div
              key={product.product_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-[#141B2D] border rounded-xl p-5 ${
                product.is_active ? "border-[#1E293B]" : "border-red-500/30 opacity-60"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getProductTypeColor(product.product_type)}`}>
                      {getProductTypeLabel(product.product_type)}
                    </span>
                    {product.tier_level && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 capitalize">
                        {product.tier_level}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-white">{product.name}</h3>
                </div>
                {!product.is_active && (
                  <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">Inactive</span>
                )}
              </div>

              <div className="text-3xl font-bold text-white mb-2">
                {formatPrice(product.price_amount, product.price_currency, product.billing_interval)}
              </div>

              {product.description && (
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">{product.description}</p>
              )}

              {product.features && product.features.length > 0 && (
                <div className="space-y-1 mb-4">
                  {product.features.slice(0, 3).map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-400">
                      <Check className="w-4 h-4 text-emerald-400" />
                      {feature}
                    </div>
                  ))}
                  {product.features.length > 3 && (
                    <p className="text-xs text-gray-500">+{product.features.length - 3} more features</p>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 text-xs mb-4">
                {product.stripe_product_id ? (
                  <span className="flex items-center gap-1 text-emerald-400">
                    <Check className="w-3 h-3" /> Synced to Stripe
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-400">
                    <Clock className="w-3 h-3" /> Not synced
                  </span>
                )}
              </div>

              <div className="flex gap-2 pt-3 border-t border-[#1E293B]">
                <button
                  onClick={() => { setEditingProduct(product); setShowForm(true); }}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#1E293B] text-white rounded-lg hover:bg-[#2D3B4F] text-sm"
                >
                  <Edit className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={() => syncToStripe(product.product_id)}
                  disabled={syncing === product.product_id}
                  className="flex items-center justify-center gap-1 px-3 py-2 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30 text-sm disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing === product.product_id ? "animate-spin" : ""}`} />
                </button>
                <button
                  onClick={() => deleteProduct(product.product_id)}
                  className="flex items-center justify-center px-3 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showForm && (
          <ProductForm
            product={editingProduct}
            onClose={() => { setShowForm(false); setEditingProduct(null); }}
            onSave={() => { setShowForm(false); setEditingProduct(null); fetchProducts(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const ProductForm = ({ product, onClose, onSave }) => {
  const isEditing = !!product;
  
  const [formData, setFormData] = useState({
    name: product?.name || "",
    description: product?.description || "",
    product_type: product?.product_type || "subscription",
    price_amount: product?.price_amount || "",
    price_currency: product?.price_currency || "usd",
    billing_interval: product?.billing_interval || "month",
    billing_interval_count: product?.billing_interval_count || 1,
    tier_level: product?.tier_level || "",
    features: product?.features || [""],
    is_active: product?.is_active !== false,
    auto_sync_stripe: true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price_amount) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const url = isEditing 
        ? `${API}/products/${product.product_id}`
        : `${API}/products`;
      
      const method = isEditing ? "PUT" : "POST";
      
      const payload = {
        ...formData,
        price_amount: parseFloat(formData.price_amount),
        features: formData.features.filter(f => f.trim() !== ""),
        billing_interval: formData.product_type === "subscription" ? formData.billing_interval : null
      };

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(isEditing ? "Product updated!" : "Product created!");
        onSave();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save product");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ""] });
  };

  const updateFeature = (index, value) => {
    const updated = [...formData.features];
    updated[index] = value;
    setFormData({ ...formData, features: updated });
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-background border border-[#1E293B] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-[#1E293B] flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {isEditing ? "Edit Product" : "Create New Product"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#1E293B] rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-white mb-3">Product Type</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: "subscription", label: "Subscription", icon: RefreshCw, desc: "Recurring billing" },
                { id: "one_time", label: "One-Time", icon: Package, desc: "Single purchase" },
                { id: "coaching_package", label: "Coaching", icon: Users, desc: "Custom package" }
              ].map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, product_type: type.id })}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    formData.product_type === type.id
                      ? "border-primary bg-primary/10"
                      : "border-[#1E293B] hover:border-[#2D3B4F]"
                  }`}
                >
                  <type.icon className={`w-6 h-6 mb-2 ${formData.product_type === type.id ? "text-primary" : "text-gray-400"}`} />
                  <p className="font-medium text-white">{type.label}</p>
                  <p className="text-xs text-gray-500">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">Product Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Elite Coaching Program"
              className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what's included..."
              rows={3}
              className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">Price *</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_amount}
                  onChange={(e) => setFormData({ ...formData, price_amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg pl-8 pr-4 py-3 text-white"
                  required
                />
              </div>
            </div>
            
            {formData.product_type === "subscription" && (
              <div>
                <label className="block text-sm font-medium text-white mb-2">Billing Interval</label>
                <select
                  value={formData.billing_interval}
                  onChange={(e) => setFormData({ ...formData, billing_interval: e.target.value })}
                  className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
                >
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>
            )}
          </div>

          {formData.product_type === "subscription" && (
            <div>
              <label className="block text-sm font-medium text-white mb-2">Tier Level</label>
              <select
                value={formData.tier_level}
                onChange={(e) => setFormData({ ...formData, tier_level: e.target.value })}
                className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
              >
                <option value="">No tier (custom)</option>
                <option value="foundations">Foundations</option>
                <option value="advanced">Advanced</option>
                <option value="elite">Elite</option>
              </select>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-white">Features</label>
              <button
                type="button"
                onClick={addFeature}
                className="text-xs text-primary hover:text-blue-400"
              >
                + Add Feature
              </button>
            </div>
            <div className="space-y-2">
              {formData.features.map((feature, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => updateFeature(i, e.target.value)}
                    placeholder="e.g., Unlimited workout access"
                    className="flex-1 bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2 text-white text-sm"
                  />
                  {formData.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(i)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {!isEditing && (
            <div className="flex items-center justify-between p-4 bg-purple-900/20 border border-purple-500/30 rounded-xl">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="text-white font-medium">Auto-sync to Stripe</p>
                  <p className="text-gray-400 text-sm">Create product in your Stripe account</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, auto_sync_stripe: !formData.auto_sync_stripe })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  formData.auto_sync_stripe ? "bg-purple-500" : "bg-[#2D3B4F]"
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                  formData.auto_sync_stripe ? "translate-x-6" : "translate-x-0.5"
                }`} />
              </button>
            </div>
          )}

          <div className="flex gap-4 pt-4 border-t border-[#1E293B]">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 disabled:opacity-50"
            >
              {saving ? "Saving..." : isEditing ? "Update Product" : "Create Product"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default ProductsManager;
